import type { Navigation, Media } from "@/payload-types";
import { resolvePayloadUrl, type PageParam } from "@/lib/url-utils";

/**
 * Navigation utilities
 *
 * These utilities provide type aliases and a simple transformation
 * function for Payload CMS navigation data.
 */

// Type aliases derived from Payload's generated types
export type NavMenuItem = NonNullable<Navigation["mainMenu"]>[number];
export type NavMenuChild = NonNullable<NavMenuItem["children"]>[number];
export type NavCTA = Navigation["primaryCTA"];
export type RightNavItem = NonNullable<Navigation["rightMenu"]>[number];

// For backwards compatibility - re-export with old names
export type NavItemConfig = NavMenuItem;
export type CTAConfig = NavCTA;
export type MegaMenuCTAConfig = NavMenuItem["megaMenuCTA"];

// Type for transformed navigation data
export type TransformedNavData = ReturnType<typeof transformNavigationData>;

/**
 * Transform the navigation global into a format for components
 * This is the main entry point - call this once in Header.tsx
 */
export function transformNavigationData(navigation: Navigation | null) {
  if (!navigation) return null;

  return {
    navItems: navigation.mainMenu || [],
    rightNavItems: navigation.rightMenu || [],
    primaryCTA: navigation.primaryCTA,
    secondaryCTA: navigation.secondaryCTA,
    logo: navigation.logo as Media | null | undefined,
    logoText: navigation.logoText,
  };
}

type AnyLinkType = "page" | "custom" | "phone" | "email" | "dropdown";

type NavItemLike = {
  type?: AnyLinkType | null;
  page?: PageParam;
  url?: string | null;
  dropdownPage?: PageParam;
  children?: ReadonlyArray<{
    type?: AnyLinkType | null;
    page?: PageParam;
    url?: string | null;
  }> | null;
};

function normalizePath(path: string): string {
  if (!path || path === "#") return "";
  if (path === "/") return "/";
  return path.replace(/\/+$/, "");
}

function linkMatchesPath(
  pathname: string,
  type: AnyLinkType | null | undefined,
  page: PageParam | undefined,
  url: string | null | undefined,
): boolean {
  if (type !== "page" && type !== "custom") return false;
  const resolved = resolvePayloadUrl(type, { page, url });
  if (!resolved || resolved === "#") return false;
  return normalizePath(resolved) === normalizePath(pathname);
}

/**
 * Returns true if `item` represents the page the user is currently on.
 * For dropdowns, also returns true when any child link or the dropdown's
 * own page matches — so the top-level label highlights on sub-pages.
 */
export function isNavItemActive(pathname: string, item: NavItemLike): boolean {
  if (!pathname) return false;

  if (item.type === "dropdown") {
    if (item.dropdownPage && linkMatchesPath(pathname, "page", item.dropdownPage, null)) {
      return true;
    }
    if (item.children) {
      return item.children.some((child) =>
        linkMatchesPath(pathname, child.type, child.page, child.url),
      );
    }
    return false;
  }

  return linkMatchesPath(pathname, item.type, item.page, item.url);
}